import { AttributeType, CatalogResourceDc, CatalogResourceType, generateId, GeometryType } from "@evergis/api";
import React, { createContext, FC, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_ID_ATTRIBUTE_NAME } from "../../../evergis/constants";
import { EditGeometryType } from "../../../evergis/core/attributes/geometryTypes";
import { useQueryAttributes } from "../EditResource/components/QueryLayerResource/hooks/useQueryAttributes";
import { useQuerySysAttributes } from "../EditResource/components/QueryLayerResource/hooks/useQuerySysAttributes";
import { useEditResource } from "../SelectFiles/Edit/useEditResource";
import { NewLayerContextType } from "./types";

const NewLayerContext = createContext<NewLayerContextType>({});

export const NewLayerProvider: FC = ({ children }) => {
  const { t } = useTranslation("common");

  const [geometryType, setGeometryType] = useState<GeometryType | EditGeometryType>(GeometryType.Point);

  const item = useMemo(
    (): CatalogResourceDc => ({
      name: t("points"),
      type: CatalogResourceType.Layer,
      geometryType,
      tags: [],
      description: "",
      subtype: geometryType === EditGeometryType.Raster ? "PostgresTileLayerService" : void 0,
    }),
    [geometryType, t],
  );
  const { name, setName, description, setDescription, tags, setTags, systemName, setSystemName } =
    useEditResource(item);
  const [path, setPath] = useState<CatalogResourceDc[]>([]);

  const {
    attributesConfiguration,
    attributes,
    setAttributes,
    updateAttribute,
    deleteAttribute,
    addAttribute,
    setDisplayField,
  } = useQueryAttributes(
    {
      attributes: [
        {
          id: generateId(),
          attributeName: "Attribute1",
          alias: "",
          columnName: "Attribute1",
          type: AttributeType.String,
          isDisplayed: true,
          isEditable: true,
        },
      ],
      idAttribute: DEFAULT_ID_ATTRIBUTE_NAME,
    },
    "id",
  );

  const [sysAttributes, { addSysAttributes, deleteSysAttribute }] = useQuerySysAttributes(
    [],
    addAttribute,
    deleteAttribute,
  );

  const value: NewLayerContextType = {
    attributesConfiguration,
    attributes,
    setAttributes,
    updateAttribute,
    deleteAttribute,
    addAttribute,
    sysAttributes,
    addSysAttributes,
    deleteSysAttribute,
    setDisplayField,
    geometryType: geometryType as GeometryType,
    setGeometryType,
    path,
    setPath,
    name,
    setName,
    description,
    setDescription,
    tags,
    setTags,
    systemName,
    setSystemName,
    item,
  };

  return <NewLayerContext.Provider value={value}>{children}</NewLayerContext.Provider>;
};

export const useNewLayerContext = (): NewLayerContextType => {
  return useContext(NewLayerContext);
};
